import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Send,
  Loader,
  AlertCircle,
  ArrowLeft,
  FileText,
  Clock,
  Users,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import { getApiBaseUrl } from "../../services/apiBase";
import "./TeacherAgents.css";

const API_BASE_URL = getApiBaseUrl();

const LessonPlannerAgent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lessonPlan, setLessonPlan] = useState(null);
  
  const [formData, setFormData] = useState({
    subject: "",
    topic: "",
    gradeLevel: "",
    duration: "45",
  
  });

  const subjects = [
    "Mathematics",
    "English",
    "Science",
    "Biology",
    "Chemistry",
    "Physics",
    "Social Studies",
    "Geography",
    "History",
    "Computer Science",
  ];

  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token")
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGeneratePlan = async () => {
    if (!formData.subject || !formData.topic || !formData.gradeLevel) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        navigate("/login");
        return;
      }

      const prompt = `Create a comprehensive lesson plan for ${formData.gradeLevel} students on the topic of "${formData.topic}" in ${formData.subject}. The lesson duration is ${formData.duration} minutes. ${formData.objectives ? `Learning objectives: ${formData.objectives}` : ""}

Please include:
1. Learning Objectives (what students will be able to do)
2. Materials Needed
3. Introduction/Hook (5-10 minutes)
4. Main Activities (with time allocations)
5. Assessment Methods
6. Closure/Summary
7. Homework/Extension Activities`;

      const response = await fetch(`${API_BASE_URL}/api/teacher/tutor/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: prompt,
          context: {
            subject: formData.subject,
            grade_level: formData.gradeLevel,
            topic: formData.topic,
          },
        }),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to generate lesson plan");
      }

      const data = await response.json();
      setLessonPlan(data.response);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Failed to generate lesson plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="teacher-agent-page">
        <div className="agent-header">
          <button
            className="back-button"
            onClick={() => navigate("/teacher-dashboard")}
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>

          <div className="header-content">
            <div className="header-icon" style={{ background: "#F5B53D" }}>
              <BookOpen size={32} />
            </div>
            <div>
              <h1>Lesson Planner</h1>
              <p>Create comprehensive lesson plans for your classes</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <p>{error}</p>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="lesson-planner-container">
          <div className="planner-form-section">
            <div className="form-card">
              <h2>
                <FileText size={20} />
                Lesson Details
              </h2>

              <div className="form-group">
                <label htmlFor="subject">
                  Subject <span className="required">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a subject</option>
                  {subjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="topic">
                  Topic <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  placeholder="e.g., Photosynthesis, Quadratic Equations"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="gradeLevel">
                  Grade Level <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="gradeLevel"
                  name="gradeLevel"
                  value={formData.gradeLevel}
                  onChange={handleInputChange}
                  placeholder="e.g., Grade 8, JSS 2, SS 3"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="duration">
                  <Clock size={16} />
                  Lesson Duration (minutes)
                </label>
                <select
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                >
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="objectives">
                  Learning Objectives (Optional)
                </label>
                <textarea
                  id="objectives"
                  name="objectives"
                  value={formData.objectives}
                  onChange={handleInputChange}
                  placeholder="What should students be able to do by the end of this lesson?"
                  rows="3"
                />
              </div>

              <button
                className="generate-button"
                onClick={handleGeneratePlan}
                disabled={loading || !formData.subject || !formData.topic || !formData.gradeLevel}
              >
                {loading ? (
                  <>
                    <Loader className="spinner" size={20} />
                    Generating Lesson Plan...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Generate Lesson Plan
                  </>
                )}
              </button>
            </div>

            {/* Quick Tips */}
            <div className="tips-card">
              <h3>💡 Tips for Better Lesson Plans</h3>
              <ul>
                <li>Be specific with your topic and grade level</li>
                <li>Include clear, measurable learning objectives</li>
                <li>Consider your students' prior knowledge</li>
                <li>Plan for different learning styles</li>
                <li>Include time for questions and assessment</li>
              </ul>
            </div>
          </div>

          <div className="planner-result-section">
            {lessonPlan ? (
              <div className="lesson-plan-result">
                <div className="result-header">
                  <h2>
                    <FileText size={24} />
                    Your Lesson Plan
                  </h2>
                  <div className="plan-meta">
                    <span>
                      <Users size={16} />
                      {formData.gradeLevel}
                    </span>
                    <span>
                      <BookOpen size={16} />
                      {formData.subject}
                    </span>
                    <span>
                      <Clock size={16} />
                      {formData.duration} min
                    </span>
                  </div>
                </div>

                <div className="lesson-plan-content">
                  <pre>{lessonPlan}</pre>
                </div>

                <div className="plan-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setLessonPlan(null);
                      setFormData({
                        subject: "",
                        topic: "",
                        gradeLevel: "",
                        duration: "45",
                        objectives: "",
                      });
                    }}
                  >
                    Create Another Plan
                  </button>
                </div>
              </div>
            ) : (
              <div className="empty-result">
                <FileText size={64} />
                <h3>No Lesson Plan Yet</h3>
                <p>
                  Fill in the form and click "Generate Lesson Plan" to create
                  your customized lesson plan.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LessonPlannerAgent;
